import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePhoneNumbers from '../hooks/usePhoneNumbers';
import useProducts from '../hooks/useProducts';
import useScrollPosition from '../hooks/useScrollPosition';
import { formatVoiceTrafficLabel } from '../utils/voiceTrafficUtils';
import {
  normalizePricingItemForRegion,
  normalizePhoneLineForTotal,
} from '../utils/pricingItemNormalizer';
import { buildPhoneNumberPricingItems } from '../utils/phoneNumberPricingItems';
import { getDefaultVoiceTrafficSettings } from '../utils/voiceTrafficDefaults';
import { getEffectiveChatUsers, getEffectiveTierUsers, getMinUsersForTier } from '../utils/tierUsers';
import {
  ENIQ_MIN_BILLABLE_USERS,
  getEniqTierDisplayName,
  isEniqIncludedInTier,
  normalizeEniqTier,
} from '../utils/eniqPricing';
import { isEniqPerUserMode } from '../config/pricingModes';
import { usePricing } from './PricingContext';
import { useTabs } from './TabsContext';
import { useUserPreferences } from './UserPreferencesContext';

// Helper function to get plan name translation
const getPlanName = (planId, t) => {
  switch (planId) {
    case 'start': return t('calculator.eniqAiPlanStart', 'Start');
    case 'lite': return t('calculator.planLite', 'Lite');
    case 'pro': return t('calculator.eniqAiPlanPro', 'Pro');
    case 'max': return t('calculator.eniqAiPlanMax', 'Max');
    case 'scale': return t('calculator.eniqAiPlanScale', 'Scale');
    default: return planId || 'Start';
  }
};

export const CalculatorContext = createContext({});

export const CalculatorProvider = ({ children, instanceTabId, tabData = {}, onStateSave }) => {
  const { t, i18n } = useTranslation();
  const { updateCurrentCalculatorState } = useTabs();
  const { region } = useUserPreferences(); // Use region from UserPreferencesContext

  const summaryRef = useRef(null);
  const initialCalculatorStateRef = useRef(JSON.stringify(tabData));
  const isInitialMountRef = useRef(true);
  const isDuplicatedTabRef = useRef(false);
  const {
    businessPhoneUsers, setBusinessPhoneUsers,
    callTrackingSessions, setCallTrackingSessions,
    aiCalls, setAiCalls,
    callbackSessions, setCallbackSessions,
    voiceTrafficMinutes, setVoiceTrafficMinutes,
    omnichannelChatUsers, setOmnichannelChatUsers,
    manualPackageSelection, setManualPackageSelection,
    selectedProducts, isProductSelected, toggleProduct, productOptions, selectedProductsData,
    reset: productsReset
  } = useProducts(tabData.products);
  
  const { phoneNumbers, setPhoneNumbers, addPhoneNumber, removePhoneNumber, updatePhoneNumber, reset: phoneNumbersReset } = usePhoneNumbers(tabData.phoneNumbers, isProductSelected, toggleProduct);
  const scrollPosition = useScrollPosition(summaryRef);

  // UI State
  const [callTrackingSettingsOpen, setCallTrackingSettingsOpen] = useState(false);
  const [callTrackingType, setCallTrackingType] = useState(tabData.callTrackingType || 'dynamic');
  const [callMeButtonEnabled, setCallMeButtonEnabled] = useState(tabData.callMeButtonEnabled || false);
  const [staticSources, setStaticSources] = useState(tabData.staticSources || 0);
  const [numberFormatsQuantity, setNumberFormatsQuantity] = useState(tabData.numberFormatsQuantity || 1);
  const [hasCustomSettings, setHasCustomSettings] = useState(tabData.hasCustomSettings || false);

  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const [nonEnglishAnalysis, setNonEnglishAnalysis] = useState(tabData.nonEnglishAnalysis || false);
  const [hasAiCustomSettings, setHasAiCustomSettings] = useState(tabData.hasAiCustomSettings || false);

  const [businessPhoneSettingsOpen, setBusinessPhoneSettingsOpen] = useState(false);
  const [businessPhoneType, setBusinessPhoneType] = useState(tabData.businessPhoneType || 'business-phone');
  const [fmcUsers, setFmcUsers] = useState(tabData.fmcUsers || 0);
  const [hasBusinessPhoneCustomSettings, setHasBusinessPhoneCustomSettings] = useState(tabData.hasBusinessPhoneCustomSettings || false);

  // Package Tier State (for package-based pricing)
  const [packageTier, setPackageTier] = useState(tabData.packageTier || 'basic');

  const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
  const [showAdvancedTier, setShowAdvancedTier] = useState(
    () => localStorage.getItem('showAdvancedTier') === 'true'
  );
  const [showPricesWithoutBonuses, setShowPricesWithoutBonuses] = useState(
    () => localStorage.getItem('showPricesWithoutBonuses') === 'true'
  );
  const [subscriptionSettingsOpen, setSubscriptionSettingsOpen] = useState(false);
  const [subscription90Days, setSubscription90Days] = useState(tabData.subscription90Days || false);
  const [subscription180Days, setSubscription180Days] = useState(tabData.subscription180Days || false);
  const [subscription12Month, setSubscription12Month] = useState(tabData.subscription12Month || false);
  const [subscriptionSplitPayment, setSubscriptionSplitPayment] = useState(
    // Respect a previously-saved tab value so toggling split-payment off
    // survives a tab switch. The region-change useEffect below still
    // overrides it when the user actually changes region.
    tabData.subscriptionSplitPayment !== undefined ? tabData.subscriptionSplitPayment : region === 'ua'
  );
  const [customDiscount360, setCustomDiscount360] = useState(tabData.customDiscount360 ?? 20);
  const [customDiscount180, setCustomDiscount180] = useState(tabData.customDiscount180 ?? 0);
  const [hasGlobalSettings, setHasGlobalSettings] = useState(tabData.hasGlobalSettings || false);
  
  // Phone Numbers Settings
  const [phoneNumbersSettingsOpen, setPhoneNumbersSettingsOpen] = useState(false);
  const [enablePhoneActivation, setEnablePhoneActivation] = useState(tabData.enablePhoneActivation || false);
  const [hasPhoneNumbersCustomSettings, setHasPhoneNumbersCustomSettings] = useState(tabData.hasPhoneNumbersCustomSettings || false);

  // Eniq AI Settings
  const [eniqAiMinutes, setEniqAiMinutes] = useState(tabData.eniqAiMinutes || 2000);
  const [eniqAiPremiumModels, setEniqAiPremiumModels] = useState(tabData.eniqAiPremiumModels !== undefined ? tabData.eniqAiPremiumModels : true);
  const [eniqTier, setEniqTier] = useState(normalizeEniqTier(tabData.eniqTier));
  // Standalone Eniq (no Business Phone / FMC anchor) has its own user count;
  // billing always uses max(eniqStandaloneUsers, ENIQ_MIN_BILLABLE_USERS).
  const [eniqStandaloneUsers, setEniqStandaloneUsers] = useState(tabData.eniqStandaloneUsers ?? ENIQ_MIN_BILLABLE_USERS);
  const [eniqAiSettingsOpen, setEniqAiSettingsOpen] = useState(false);
  const [hasEniqAiCustomSettings, setHasEniqAiCustomSettings] = useState(tabData.hasEniqAiCustomSettings || false);
  const [eniqAiImplementationFee, setEniqAiImplementationFee] = useState(tabData.eniqAiImplementationFee || 0);
  const [enableEniqImplementationFee, setEnableEniqImplementationFee] = useState(tabData.enableEniqImplementationFee || false);

  // VoiceBot Settings
  const [voicebotMinutes, setVoicebotMinutes] = useState(Math.max(tabData.voicebotMinutes || 1100, 1100));
  const [voicebotAiMode, setVoicebotAiMode] = useState(tabData.voicebotAiMode !== undefined ? tabData.voicebotAiMode : true);
  const [voicebotSettingsOpen, setVoicebotSettingsOpen] = useState(false);
  const [hasVoicebotCustomSettings, setHasVoicebotCustomSettings] = useState(tabData.hasVoicebotCustomSettings || false);
  const [voicebotImplementationFee, setVoicebotImplementationFee] = useState(tabData.voicebotImplementationFee || 0);
  const [enableVoicebotImplementationFee, setEnableVoicebotImplementationFee] = useState(tabData.enableVoicebotImplementationFee || false);
  const [voicebotExtraChannels, setVoicebotExtraChannels] = useState(tabData.voicebotExtraChannels || 0);

  // Voice Traffic Settings - NEW: Two modes architecture with region-based defaults
  const getInitialVoiceTrafficSettings = () => {
    if (tabData.voiceTrafficCountriesMinutes || tabData.voiceTrafficUseCustomAmount !== undefined || tabData.voiceTrafficCustomAmount !== undefined) {
      // Use existing data if available
      const defaults = getDefaultVoiceTrafficSettings(region);
      return {
        voiceTrafficCountriesMinutes: tabData.voiceTrafficCountriesMinutes || defaults.voiceTrafficCountriesMinutes,
        voiceTrafficUseCustomAmount: tabData.voiceTrafficUseCustomAmount !== undefined ? tabData.voiceTrafficUseCustomAmount : defaults.voiceTrafficUseCustomAmount,
        voiceTrafficCustomAmount: tabData.voiceTrafficCustomAmount !== undefined ? tabData.voiceTrafficCustomAmount : defaults.voiceTrafficCustomAmount
      };
    }
    // Use region-based defaults for new tabs
    return getDefaultVoiceTrafficSettings(region);
  };

  const initialVoiceTrafficSettings = getInitialVoiceTrafficSettings();
  const [voiceTrafficCountriesMinutes, setVoiceTrafficCountriesMinutes] = useState(
    initialVoiceTrafficSettings.voiceTrafficCountriesMinutes
  );
  const [voiceTrafficUseCustomAmount, setVoiceTrafficUseCustomAmount] = useState(
    initialVoiceTrafficSettings.voiceTrafficUseCustomAmount
  );
  const [voiceTrafficCustomAmount, setVoiceTrafficCustomAmount] = useState(
    initialVoiceTrafficSettings.voiceTrafficCustomAmount
  );
  const [voiceTrafficSettingsOpen, setVoiceTrafficSettingsOpen] = useState(false);
  const [hasVoiceTrafficCustomSettings, setHasVoiceTrafficCustomSettings] = useState(tabData.hasVoiceTrafficCustomSettings || false);

  // Helper functions for voice traffic
  const getVoiceTrafficCountries = useCallback(() => {
    return Object.keys(voiceTrafficCountriesMinutes);
  }, [voiceTrafficCountriesMinutes]);

  const getVoiceTrafficTotalMinutes = useCallback(() => {
    if (voiceTrafficUseCustomAmount) return 0; // Custom amount mode doesn't use minutes
    return Object.values(voiceTrafficCountriesMinutes).reduce((total, minutes) => total + minutes, 0);
  }, [voiceTrafficCountriesMinutes, voiceTrafficUseCustomAmount]);

  const updateVoiceTrafficCountryMinutes = useCallback((countryCode, minutes) => {
    setVoiceTrafficCountriesMinutes(prev => ({
      ...prev,
      [countryCode]: minutes
    }));
  }, []);

  const addVoiceTrafficCountry = useCallback((countryCode) => {
    setVoiceTrafficCountriesMinutes(prev => ({
      ...prev,
      [countryCode]: 500
    }));
  }, []);

  const removeVoiceTrafficCountry = useCallback((countryCode) => {
    setVoiceTrafficCountriesMinutes(prev => {
      const updated = { ...prev };
      delete updated[countryCode];
      return updated;
    });
  }, []);

  const updateVoiceTrafficUseCustomAmount = useCallback((useCustomAmount) => {
    setVoiceTrafficUseCustomAmount(useCustomAmount);
  }, []);

  const updateVoiceTrafficCustomAmount = useCallback((amount) => {
    setVoiceTrafficCustomAmount(amount);
  }, []);

  const updateVoiceTrafficCountriesMinutes = useCallback((countriesMinutes) => {
    setVoiceTrafficCountriesMinutes(countriesMinutes);
  }, []);

  // Get custom amount currency symbol based on region
  const getVoiceTrafficCustomCurrency = useCallback(() => {
    if (region === 'eu') return '€';
    return '$'; // USD for UA, Central Asia, Other
  }, [region]);

  // Helper function to get country name from country code
  const getCountryName = useCallback((countryCode) => {
    const countryNames = {
      'UA': t('countries.ukraine', 'Ukraine'),
      'KZ': t('countries.kazakhstan', 'Kazakhstan'),
      'PL': t('countries.poland', 'Poland')
    };
    return countryNames[countryCode] || countryCode;
  }, [t]);

  // Very Custom Product Discounts
  const [veryCustomProductDiscounts, setVeryCustomProductDiscounts] = useState(tabData.veryCustomProductDiscounts || {});

  // Effect to reset all state when tab changes
  useEffect(() => {
    // Check if this is a duplicated tab
    if (tabData._isDuplicated) {
      isDuplicatedTabRef.current = true;
    }

    // Don't run on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Reset simple states (region is now managed by UserPreferencesContext)
    setCallTrackingType(tabData.callTrackingType || 'dynamic');
    setCallMeButtonEnabled(tabData.callMeButtonEnabled || false);
    setStaticSources(tabData.staticSources || 0);
    setNumberFormatsQuantity(tabData.numberFormatsQuantity || 1);
    setHasCustomSettings(tabData.hasCustomSettings || false);
    setNonEnglishAnalysis(tabData.nonEnglishAnalysis || false);
    setHasAiCustomSettings(tabData.hasAiCustomSettings || false);
    setBusinessPhoneType(tabData.businessPhoneType || 'business-phone');
    setFmcUsers(tabData.fmcUsers || 0);
    setHasBusinessPhoneCustomSettings(tabData.hasBusinessPhoneCustomSettings || false);
    setPackageTier(tabData.packageTier || 'basic');
    const sub = tabData.subscriptionSettings || tabData;
    if (typeof sub.subscription90Days === 'boolean') setSubscription90Days(sub.subscription90Days);
    if (typeof sub.subscription180Days === 'boolean') setSubscription180Days(sub.subscription180Days);
    if (typeof sub.subscription12Month === 'boolean') setSubscription12Month(sub.subscription12Month);
    // Preserve the user's saved choice when switching tabs; fall back to the
    // region default only for fresh tabs without persisted state.
    if (typeof sub.subscriptionSplitPayment === 'boolean') {
      setSubscriptionSplitPayment(sub.subscriptionSplitPayment);
    } else {
      setSubscriptionSplitPayment(region === 'ua');
    }
    setCustomDiscount360(tabData.customDiscount360 ?? 20);
    setCustomDiscount180(tabData.customDiscount180 ?? 0);
    setHasGlobalSettings(tabData.hasGlobalSettings || false);
    setEnablePhoneActivation(tabData.enablePhoneActivation || false);
    setHasPhoneNumbersCustomSettings(tabData.hasPhoneNumbersCustomSettings || false);
    setEniqAiMinutes(tabData.eniqAiMinutes || 2000);
    setEniqAiPremiumModels(tabData.eniqAiPremiumModels !== undefined ? tabData.eniqAiPremiumModels : true);
    setEniqTier(normalizeEniqTier(tabData.eniqTier));
    setEniqStandaloneUsers(tabData.eniqStandaloneUsers ?? ENIQ_MIN_BILLABLE_USERS);
    setHasEniqAiCustomSettings(tabData.hasEniqAiCustomSettings || false);
    setVoicebotMinutes(Math.max(tabData.voicebotMinutes || 1100, 1100));
    setVoicebotAiMode(tabData.voicebotAiMode !== undefined ? tabData.voicebotAiMode : true);
    setHasVoicebotCustomSettings(tabData.hasVoicebotCustomSettings || false);
    setVoicebotImplementationFee(tabData.voicebotImplementationFee || 0);
    setEnableVoicebotImplementationFee(tabData.enableVoicebotImplementationFee || false);
    setVoicebotExtraChannels(tabData.voicebotExtraChannels || 0);
    const defaultVTSettings = getDefaultVoiceTrafficSettings(region);
    setVoiceTrafficCountriesMinutes(tabData.voiceTrafficCountriesMinutes || defaultVTSettings.voiceTrafficCountriesMinutes);
    setVoiceTrafficUseCustomAmount(tabData.voiceTrafficUseCustomAmount !== undefined ? tabData.voiceTrafficUseCustomAmount : defaultVTSettings.voiceTrafficUseCustomAmount);
    setVoiceTrafficCustomAmount(tabData.voiceTrafficCustomAmount !== undefined ? tabData.voiceTrafficCustomAmount : defaultVTSettings.voiceTrafficCustomAmount);
    setHasVoiceTrafficCustomSettings(tabData.hasVoiceTrafficCustomSettings || false);
    setVeryCustomProductDiscounts(tabData.veryCustomProductDiscounts || {});

    // Reset hook states
    productsReset(tabData.products);
    phoneNumbersReset(tabData.phoneNumbers);

  }, [instanceTabId, productsReset, phoneNumbersReset]);

  // Effect to handle region changes - convert FMC to business-phone for non-Ukraine regions
  useEffect(() => {
    const isUkraineRegion = region === 'ua' ||
                          String(region).toLowerCase().includes('ukr') ||
                          String(region).toLowerCase().includes('ua') ||
                          String(region).toLowerCase() === 'ukraine';

    // If region is not Ukraine and businessPhoneType contains FMC, convert to business-phone
    if (!isUkraineRegion && (businessPhoneType === 'fmc' || businessPhoneType === 'business-phone-fmc')) {
      setBusinessPhoneType('business-phone');
      setFmcUsers(0); // Reset FMC users
      setHasBusinessPhoneCustomSettings(false); // Reset custom settings
    }

    // FMC-only mode: deactivate Omnichannel Chat (no smartphone app = no chat)
    if (businessPhoneType === 'fmc' && isProductSelected('omnichannel-chat')) {
      toggleProduct('omnichannel-chat');
    }
  }, [region, businessPhoneType, isProductSelected, toggleProduct]);

  // Reset split-payment toggle to region default when region actually changes.
  // Why: split payment (mono/PrivatBank) is UA-only — switching region should match its default.
  const prevRegionForSplitRef = useRef(region);
  useEffect(() => {
    if (prevRegionForSplitRef.current !== region) {
      setSubscriptionSplitPayment(region === 'ua');
      prevRegionForSplitRef.current = region;
    }
  }, [region]);

  // When Business Phone is removed, auto-remove AI/Callback/Omnichannel
  // (these products are tied to BP users, so without BP they have no basis).
  const prevBusinessPhoneSelectedRef = useRef(isProductSelected('business-phone'));
  useEffect(() => {
    const isBPSelected = isProductSelected('business-phone');
    if (prevBusinessPhoneSelectedRef.current && !isBPSelected) {
      // BP was just removed — clear dependent products
      if (isProductSelected('ai-conversation')) toggleProduct('ai-conversation');
      if (isProductSelected('callback')) toggleProduct('callback');
      if (isProductSelected('omnichannel-chat')) toggleProduct('omnichannel-chat');
    }
    prevBusinessPhoneSelectedRef.current = isBPSelected;
  }, [isProductSelected, toggleProduct]);

  // The package tier selector lives inside the Business Phone card; without BP
  // (in either BP or FMC mode) a pro/corporate tier must not survive — it would
  // keep marking callback/chat/Eniq as "included" with no UI left to change it.
  // Level-triggered (not edge-triggered) on purpose: every hydration path sets
  // packageTier and selectedProducts in the same batch, so this only fires when
  // the rendered state is genuinely inconsistent, and it also heals stale tabs
  // saved before this fix.
  useEffect(() => {
    if (!isProductSelected('business-phone') && packageTier !== 'basic') {
      setPackageTier('basic');
    }
  }, [isProductSelected, packageTier]);

  // Helper: products bundled (free) for a given tier
  const getBundledProductsForTier = (tier) => {
    // Corporate includes Eniq AI Standard instead of AI Conversation Intelligence
    if (tier === 'corporate') return ['callback', 'omnichannel-chat', 'eniq-ai'];
    if (tier === 'pro') return ['ai-conversation', 'callback', 'omnichannel-chat'];
    if (tier === 'advanced') return ['callback'];
    return [];
  };

  // Track products that were added while their tier had them bundled (implicit adds).
  // Implicit add = user clicked add when product was already free → remove on tier downgrade.
  // Explicit add = user added when product was paid → keep across tier changes.
  const [tierAutoAddedProducts, setTierAutoAddedProducts] = useState(new Set());
  const prevSelectedIdsRef = useRef(new Set(selectedProducts.filter(p => p.active).map(p => p.id)));
  useEffect(() => {
    const currentSet = new Set(selectedProducts.filter(p => p.active).map(p => p.id));
    const prevSet = prevSelectedIdsRef.current;
    const bundledNow = getBundledProductsForTier(packageTier);

    setTierAutoAddedProducts(prev => {
      const next = new Set(prev);
      // Newly added — if currently bundled, mark as auto-added
      for (const id of currentSet) {
        if (!prevSet.has(id) && bundledNow.includes(id)) next.add(id);
      }
      // Removed — clear from set
      for (const id of prevSet) {
        if (!currentSet.has(id)) next.delete(id);
      }
      return next;
    });

    prevSelectedIdsRef.current = currentSet;
  }, [selectedProducts, packageTier]);

  // When tier changes, remove auto-added products that are no longer bundled in the new tier
  const prevTierForCleanupRef = useRef(packageTier);
  useEffect(() => {
    if (prevTierForCleanupRef.current !== packageTier) {
      const bundledNow = getBundledProductsForTier(packageTier);
      tierAutoAddedProducts.forEach(id => {
        if (!bundledNow.includes(id) && isProductSelected(id)) {
          toggleProduct(id);
        }
      });
    }
    prevTierForCleanupRef.current = packageTier;
  }, [packageTier, tierAutoAddedProducts, isProductSelected, toggleProduct]);

  // NOTE: Auto-activation of included products for Pro/Corporate has been removed.
  // Products included in Pro/Corporate tiers (AI, Callback, Chat) are now shown
  // in AddProductSection with visual highlighting, allowing manual addition for
  // price comparison purposes. The bundle API still handles $0 pricing correctly.

  // Effect to enforce minimum users when tier changes
  useEffect(() => {
    const minUsers = getMinUsersForTier(packageTier);
    if (businessPhoneUsers < minUsers) {
      setBusinessPhoneUsers(minUsers);
    }
  }, [packageTier, businessPhoneUsers, setBusinessPhoneUsers]);

  const subscriptionSettings = useMemo(() => ({
    show90Days: subscription90Days,
    show180Days: subscription180Days,
    show12MonthContract: subscription12Month,
    showSplitPayment: subscriptionSplitPayment,
  }), [subscription90Days, subscription180Days, subscription12Month, subscriptionSplitPayment]);

  // Real QSLN Formula (Poisson distribution for telephone lines)
  const calculateQSLN = useCallback((traf) => {
    if (traf <= 0) return 0;

    // Convert traffic to coefficient: |traf| / 10 * 0.0833
    const traf_koef = Math.abs(traf) / 10 * 0.0833;

    if (traf_koef === 0 || isNaN(traf_koef)) return 0;
    
    let p0 = 1;
    let plast = 1;
    
    for (let i = 1; i < 10000; i++) {
      plast = plast * traf_koef / i;
      p0 = p0 + plast;
      
      if (i > 10000) break;
      
      // Check if convergence criterion is met
      if ((plast / p0) < 0.05) {
        const nums = (i > traf_koef * 1.05) ? i : Math.ceil(traf_koef * 1.05);
        return nums;
      }
    }
    
    return Math.ceil(traf_koef * 1.05);
  }, []);

  // Memoized Calculation Hooks
  
  // NEW: Calculate slots for PRICING (without Call Me Button reduction)
  const calculatePricingSlots = useCallback(() => {
    if (!isProductSelected('call-tracking')) return 0;
    
    if (callTrackingType === 'static') {
      // Static: just return static sources * formats (no Call Me applied to static)
      return staticSources * numberFormatsQuantity;
    } else if (callTrackingType === 'combined') {
      // Combined: static sources (no formats) + dynamic slots * formats
      const dynamicSlots = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
      return staticSources + dynamicSlots;
    } else {
      // Dynamic only: QSLN * formats (no Call Me reduction for pricing)
      return calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
    }
  }, [isProductSelected, callTrackingType, staticSources, callTrackingSessions, calculateQSLN, numberFormatsQuantity]);

  // NEW: Call Me Button formula (replace the simple division)
  const getCalltrackingNumbersQtyWithCallMe = useCallback((dailyTraf, numberFormatsQty) => {
    const numberOfSimultaneouslyCalls = (dailyTraf / 24 * 10) / 12 * 0.01;
    const result = numberOfSimultaneouslyCalls * 1.5;
    if (result > 5) {
      return Math.round(result) * numberFormatsQty;
    } else {
      return 5 * numberFormatsQty;
    }
  }, []);

  // NEW: Calculate actual phone numbers needed (with Call Me Button reduction)
  const calculateActualPhoneNumbers = useCallback(() => {
    if (!isProductSelected('call-tracking')) return 0;
    
    if (callTrackingType === 'static') {
      // Static: Call Me doesn't apply, just return static sources * formats
      return staticSources * numberFormatsQuantity;
    } else if (callTrackingType === 'combined') {
      // Combined: static sources (no formats) + Call Me applied to dynamic part
      let dynamicNumbers = 0;
      if (callMeButtonEnabled) {
        // Use new Call Me formula for dynamic part
        dynamicNumbers = getCalltrackingNumbersQtyWithCallMe(callTrackingSessions || 0, numberFormatsQuantity);
      } else {
        // No Call Me: apply formats to dynamic slots
        dynamicNumbers = calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
      }
      
      return staticSources + dynamicNumbers;
    } else {
      // Dynamic only
      if (callMeButtonEnabled) {
        // Use new Call Me formula
        return getCalltrackingNumbersQtyWithCallMe(callTrackingSessions || 0, numberFormatsQuantity);
      }
      
      // No Call Me: apply formats to dynamic slots
      return calculateQSLN(callTrackingSessions || 0) * numberFormatsQuantity;
    }
  }, [isProductSelected, callTrackingType, staticSources, callTrackingSessions, callMeButtonEnabled, getCalltrackingNumbersQtyWithCallMe, calculateQSLN, numberFormatsQuantity]);

  // LEGACY: Keep for backward compatibility (now uses actual phone numbers for UI display)
  const calculatePhoneNumbersNeeded = useCallback(() => {
    return calculateActualPhoneNumbers();
  }, [calculateActualPhoneNumbers]);

  const { calculateBundle, getCurrencySymbol, currencyRates } = usePricing();
  
  // Calculate pricing using backend API
  const [priceCalculation, setPriceCalculation] = useState({
    currency: '$',
    businessPhonePrice: 0,
    callTrackingPrice: 0,
    aiPrice: 0,
    callbackPrice: 0,
    phoneNumbersTotal: 0,
    voiceTrafficPrice: 0,
    totalMonthlyCost: 0,
    summaryItems: [],
    subscriptionPrices: {},
    total: 0,
    phoneNumbersNeeded: { total: 0 }
  });

  // Create stable dependencies using useMemo to prevent infinite loops
  const stableSelectedProducts = useMemo(() =>
    JSON.stringify(selectedProducts.map(p => ({ id: p.id, active: p.active })))
  , [selectedProducts]);

  const stablePhoneNumbers = useMemo(() =>
    JSON.stringify(phoneNumbers.map(p => ({ name: p.name, quantity: p.quantity, price: p.price })))
  , [phoneNumbers]);

  // Calculate all prices when dependencies change (with debouncing for performance)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const calculatePrices = async () => {
      try {
        

        // Prepare products array for bundle calculation
        const bundleProducts = [];
        const summaryItems = []; // For subscription calculator - base prices
        const summaryItemsWithDiscounts = []; // For display - prices with bundle discounts
        const individualPrices = {};
        let currency = getCurrencySymbol(region); // Fallback
        let totalUSD = 0;

        

          // Build products array for bundle API
          // Business Phone (only add if not standalone FMC)

          if (isProductSelected('business-phone') && businessPhoneType !== 'fmc') {
            // Minimum users depends on tier: 10 for Corporate/Advanced, 3 for others
            const minUsers = getMinUsersForTier(packageTier);
            const quantity = Math.max(businessPhoneUsers, minUsers);
            bundleProducts.push({
              product_id: 'business_phone',
              region,
              quantity: quantity,
              business_phone_type: businessPhoneType,
              package_tier: packageTier || ''
            });
            
            // FMC if needed (only for Ukraine region)
            const isUkraineRegion = region === 'ua' || 
                                   String(region).toLowerCase().includes('ukr') || 
                                   String(region).toLowerCase().includes('ua') || 
                                   String(region).toLowerCase() === 'ukraine';
            
            if ((businessPhoneType === 'business-phone-fmc') && fmcUsers > 0 && isUkraineRegion) {
              bundleProducts.push({
                product_id: 'fmc',
                region,
                quantity: fmcUsers, // No minimum for combined
                business_phone_type: businessPhoneType,
                package_tier: packageTier || ''
              });
            }
          } else {
          }
          
          // Standalone FMC (only for Ukraine region)
          if (isProductSelected('business-phone') && businessPhoneType === 'fmc') {
            const isUkraineRegion = region === 'ua' ||
                                   String(region).toLowerCase().includes('ukr') ||
                                   String(region).toLowerCase().includes('ua') ||
                                   String(region).toLowerCase() === 'ukraine';

            if (fmcUsers > 0 && isUkraineRegion) {
              bundleProducts.push({
                product_id: 'fmc',
                region,
                quantity: Math.max(fmcUsers, 6), // Minimum 6 for standalone
                business_phone_type: businessPhoneType,
                package_tier: packageTier || ''
              });
            }
          }

        // Call Tracking
        if (isProductSelected('call-tracking')) {
          const pricingSlots = calculatePricingSlots(); // Use pricing slots (no Call Me reduction)
          bundleProducts.push({
            product_id: 'call_tracking',
            region,
            quantity: pricingSlots, // Send pricing slots (for backend pricing calculation)
            actual_phone_numbers: calculateActualPhoneNumbers(), // Send actual numbers needed (for UI display)
            call_tracking_type: callTrackingType,
            static_sources: staticSources,
            call_me_button: callMeButtonEnabled,
            number_formats_quantity: numberFormatsQuantity
          });
        }

          // AI Conversation
          // For Pro/Corporate tiers, AI is included - still add to bundle to show $0 price
          // For Advanced tier, AI is NOT auto-included (only added if user selected it)
          if (isProductSelected('ai-conversation') || packageTier === 'pro') {
            const aiQuantity = (packageTier === 'pro' || packageTier === 'basic' || packageTier === 'advanced' || packageTier === 'corporate')
              ? getEffectiveTierUsers({ businessPhoneUsers, fmcUsers, businessPhoneType, packageTier })
              : aiCalls;

            bundleProducts.push({
              product_id: 'ai_conversation',
              region,
              quantity: aiQuantity,
              package_tier: packageTier || ''
            });
          }

          // Callback
          // For Pro/Corporate/Advanced tiers, Callback is included - still add to bundle to show $0 price
          if (isProductSelected('callback') || (packageTier === 'pro' || packageTier === 'corporate' || packageTier === 'advanced')) {
            bundleProducts.push({
              product_id: 'callback',
              region,
              quantity: Math.max(Number(callbackSessions) || 0, 100),
              package_tier: packageTier || ''
            });
          }

        // Voice Traffic - NEW: Two modes support
        if (isProductSelected('voice-traffic')) {
          // Debug current voice traffic state before adding to bundle (logs removed for production)
          if (voiceTrafficUseCustomAmount) {
            // Custom Amount Mode - only send if amount > 0
            if (voiceTrafficCustomAmount > 0) {
              const voiceTrafficProduct = {
                product_id: 'voice_traffic',
                region,
                use_custom_amount: true,
                custom_amount: voiceTrafficCustomAmount
              };
              bundleProducts.push(voiceTrafficProduct);
            } else {
            }
          } else {
            // Package Mode - countries with minutes
            // Ensure values are numbers (Go backend expects map[string]int)
            const sanitizedCountriesMinutes = Object.fromEntries(
              Object.entries(voiceTrafficCountriesMinutes || {}).map(([code, mins]) => [
                code,
                typeof mins === 'string' ? (parseInt(mins, 10) || 0) : (Number.isFinite(mins) ? mins : 0)
              ])
            );

            // Only send if we have valid countries with minutes > 0
            const hasValidCountries = Object.values(sanitizedCountriesMinutes).some(mins => mins > 0);

            if (hasValidCountries) {
              bundleProducts.push({
                product_id: 'voice_traffic',
                region,
                use_custom_amount: false,
                voice_traffic_countries_minutes: sanitizedCountriesMinutes
              });
            } else {
            }
          }
        }

          // Dual-mode: also push packages when deposit is enabled (if user customized VT)
          if (isProductSelected('voice-traffic') && voiceTrafficUseCustomAmount) {
            const sanitizedCountriesMinutes = Object.fromEntries(
              Object.entries(voiceTrafficCountriesMinutes || {}).map(([code, mins]) => [
                code,
                typeof mins === 'string' ? (parseInt(mins, 10) || 0) : (Number.isFinite(mins) ? mins : 0)
              ])
            );
            const hasValidCountries = Object.values(sanitizedCountriesMinutes).some(mins => mins > 0);
            const defaults = getDefaultVoiceTrafficSettings(region);
            const isDifferentFromRegionDefaults = JSON.stringify(sanitizedCountriesMinutes) !== JSON.stringify(defaults.voiceTrafficCountriesMinutes);
            if ((isDifferentFromRegionDefaults || hasVoiceTrafficCustomSettings) && hasValidCountries) {
              bundleProducts.push({
                product_id: 'voice_traffic',
                region,
                use_custom_amount: false,
                voice_traffic_countries_minutes: sanitizedCountriesMinutes
              });
            }
          }

          // Omnichannel Chat
          // Skip if FMC-only mode (no smartphone app = no chat)
          // For Pro/Corporate tiers, Omnichannel Chat is included - still add to bundle to show $0 price
          // For Advanced tier, Chat is NOT auto-included (only added if user selected it)
          if (businessPhoneType !== 'fmc' &&
              (isProductSelected('omnichannel-chat') || (packageTier === 'pro' || packageTier === 'corporate'))) {
            // For Pro/Corporate: quantity = effective users (but price will be $0)
            // For Basic/Advanced: quantity = effective users (locked to Business Phone users)
            // For legacy (no tier): quantity = omnichannelChatUsers
            const chatQuantity = (packageTier === 'pro' || packageTier === 'corporate' || packageTier === 'basic' || packageTier === 'advanced')
              ? getEffectiveChatUsers({ businessPhoneUsers, businessPhoneType, packageTier })
              : omnichannelChatUsers;

            bundleProducts.push({
              product_id: 'omnichannel_chat',
              region,
              quantity: chatQuantity,
              package_tier: packageTier || ''
            });
          }

          // Eniq AI
          // Corporate includes Eniq AI Standard - still add to bundle to show $0 price
          if (isProductSelected('eniq-ai') || packageTier === 'corporate') {
            // Per-user model: Eniq users follow Business Phone / FMC users,
            // but Eniq always bills at least ENIQ_MIN_BILLABLE_USERS (5) —
            // standalone AND anchored (BP basic/pro can carry only 3 users).
            const hasUserBase = isProductSelected('business-phone') || isProductSelected('fmc');
            const eniqUsers = hasUserBase
              ? Math.max(
                  getEffectiveTierUsers({ businessPhoneUsers, fmcUsers, businessPhoneType, packageTier }),
                  ENIQ_MIN_BILLABLE_USERS
                )
              : Math.max(eniqStandaloneUsers, ENIQ_MIN_BILLABLE_USERS);

            bundleProducts.push({
              product_id: 'eniq_ai',
              region,
              quantity: isEniqPerUserMode() ? eniqUsers : eniqAiMinutes,
              package_tier: packageTier || '',
              eniq_tier: eniqTier,
              premium_models: eniqAiPremiumModels
            });
          }

          // VoiceBot
          if (isProductSelected('voicebot')) {
            bundleProducts.push({
              product_id: 'voicebot',
              region,
              quantity: voicebotMinutes,
              premium_models: voicebotAiMode,
              extra_channels: voicebotExtraChannels
            });
          }



        // Calculate bundle pricing if we have products
        if (bundleProducts.length > 0) {
          try {
            const bundleRequest = {
              region,
              products: bundleProducts,
              very_custom_product_discounts: veryCustomProductDiscounts
            };
            
            const bundleResult = await calculateBundle(bundleRequest);
            
            
            // Set currency from bundle response
            if (bundleResult.total && bundleResult.total.primary && bundleResult.total.primary.symbol) {
              currency = bundleResult.total.primary.symbol;
            }
            
            // Process bundle products
            bundleResult.products.forEach(product => {
              const productId = product.product_id;
              const basePrice = product.base_price;
              const finalPrice = product.final_price; // Price after bundle discounts
              
              // Store individual prices (with bundle discounts applied)
              switch (productId) {
                case 'business_phone':
                  individualPrices.businessPhone = finalPrice;
                  break;
                case 'fmc':
                  individualPrices.fmc = finalPrice;
                  break;
                case 'call_tracking':
                  individualPrices.callTracking = finalPrice;
                  break;
                case 'ai_conversation':
                  individualPrices.ai = finalPrice;
                  break;
                case 'callback':
                  individualPrices.callback = finalPrice;
                  break;
                case 'voice_traffic':
                  individualPrices.voiceTraffic = finalPrice;
                  break;
                case 'omnichannel_chat':
                  individualPrices.omnichannelChat = finalPrice;
                  break;
                case 'eniq_ai':
                  individualPrices.eniqAi = finalPrice;
                  break;
                case 'voicebot':
                  individualPrices.voicebot = finalPrice;
                  break;
                default:
                  break;
              }
              
              // Create summary items based on product type
              if (productId === 'business_phone') {
                const users = Math.max(businessPhoneUsers, 3);
                // Format tier label with capitalized first letter
                const tierLabel = packageTier ? packageTier.charAt(0).toUpperCase() + packageTier.slice(1) : '';
                const productLabel = `${t('calculator.businessPhone', 'Business Phone')} ${tierLabel}`.trim();
                summaryItems.push({
                  label: `${productLabel} (${users} ${t('calculator.businessPhoneUsers', 'users')})`,
                  price: basePrice, // Base price for subscription calculator
                  productType: 'business-phone',
                  users: users,
                  tier: packageTier // Package tier: basic, pro, corporate
                });
                summaryItemsWithDiscounts.push({
                  label: `${productLabel} (${users} ${t('calculator.businessPhoneUsers', 'users')})`,
                  price: finalPrice, // Final price with bundle discounts
                  productType: 'business-phone',
                  users: users,
                  tier: packageTier, // Package tier: basic, pro, corporate
                  bundleDiscount: product.bundle_discount || 0
                });
              } else if (productId === 'fmc') {
                const users = businessPhoneType === 'fmc' ? Math.max(fmcUsers, 6) : fmcUsers;
                // Format tier label with capitalized first letter
                const tierLabel = packageTier ? packageTier.charAt(0).toUpperCase() + packageTier.slice(1) : '';
                const productLabel = `FMC ${tierLabel}`.trim();
                summaryItems.push({
                  label: `${productLabel} (${users} ${t('calculator.fmcUsers', 'users')})`,
                  price: basePrice,
                  productType: 'fmc',
                  users: users,
                  tier: packageTier // Package tier: basic, pro, corporate
                });
                summaryItemsWithDiscounts.push({
                  label: `${productLabel} (${users} ${t('calculator.fmcUsers', 'users')})`,
                  price: finalPrice,
                  productType: 'fmc',
                  users: users,
                  tier: packageTier, // Package tier: basic, pro, corporate
                  bundleDiscount: product.bundle_discount || 0
                });
              } else if (productId === 'call_tracking') {
                const actualNumbers = calculateActualPhoneNumbers(); // Display actual phone numbers needed
                summaryItems.push({
                  label: `${t('calculator.callTracking', 'Call Tracking')} (${actualNumbers} ${t('calculator.callTrackingSlots', 'slots')})`,
                  price: basePrice,
                  productType: 'call-tracking',
                  slots: actualNumbers,
                  sessions: callTrackingSessions
                });
                summaryItemsWithDiscounts.push({
                  label: `${t('calculator.callTracking', 'Call Tracking')} (${actualNumbers} ${t('calculator.callTrackingSlots', 'slots')})`,
                  price: finalPrice,
                  productType: 'call-tracking',
                  slots: actualNumbers,
                  sessions: callTrackingSessions,
                  bundleDiscount: product.bundle_discount || 0
                });
              } else if (productId === 'ai_conversation') {
                // For Pro tier, AI is included - show simple label
                // Advanced and Corporate: AI is a paid add-on (NOT included)
                const isAiIncludedInTier = packageTier === 'pro';
                const isBasicTier = packageTier === 'basic' || packageTier === 'advanced' || packageTier === 'corporate';

                // Calculate effective AI users for Basic tier (= BP/FMC users)
                const effectiveAiUsers = getEffectiveTierUsers({ businessPhoneUsers, fmcUsers, businessPhoneType, packageTier });

                // Label: Pro/Corporate = no quantity, Basic = users, Legacy = minutes
                let aiLabel;
                if (isAiIncludedInTier) {
                  aiLabel = t('calculator.aiConversationLabel', 'AI Conversation Intelligence');
                } else if (isBasicTier) {
                  aiLabel = `${t('calculator.aiConversationLabel', 'AI Conversation Intelligence')} (${effectiveAiUsers} ${t('calculator.businessPhoneUsers', 'users')})`;
                } else {
                  aiLabel = `${t('calculator.aiConversationLabel', 'AI Conversation Intelligence')} (${aiCalls} ${t('calculator.minutes', 'minutes')})`;
                }

                summaryItems.push({
                  label: aiLabel,
                  price: finalPrice,
                  productType: 'ai-conversation',
                  users: isBasicTier ? effectiveAiUsers : undefined,
                  minutes: !isBasicTier ? aiCalls : undefined,
                  calls: aiCalls,
                  includedInTier: isAiIncludedInTier
                });
                summaryItemsWithDiscounts.push({
                  label: aiLabel,
                  price: finalPrice,
                  productType: 'ai-conversation',
                  users: isBasicTier ? effectiveAiUsers : undefined,
                  minutes: !isBasicTier ? aiCalls : undefined,
                  calls: aiCalls,
                  bundleDiscount: product.bundle_discount || 0,
                  includedInTier: isAiIncludedInTier
                });
              } else if (productId === 'callback') {
                const cbSessions = Math.max(Number(callbackSessions) || 0, 100);
                // For Pro/Corporate/Advanced tiers, Callback is included - show simple label
                const isCallbackIncludedInTier = packageTier === 'pro' || packageTier === 'corporate' || packageTier === 'advanced';
                const callbackLabel = isCallbackIncludedInTier
                  ? t('calculator.callback', 'Callback')
                  : `${t('calculator.callback', 'Callback')} (${cbSessions} ${t('calculator.callbackSessions', 'sessions')})`;
                // For included products, use finalPrice (should be 0 from backend)
                const callbackPrice = isCallbackIncludedInTier ? finalPrice : basePrice;
                summaryItems.push({
                  label: callbackLabel,
                  price: callbackPrice,
                  productType: 'callback',
                  sessions: cbSessions,
                  includedInTier: isCallbackIncludedInTier
                });
                summaryItemsWithDiscounts.push({
                  label: callbackLabel,
                  price: finalPrice,
                  productType: 'callback',
                  sessions: cbSessions,
                  bundleDiscount: product.bundle_discount || 0,
                  includedInTier: isCallbackIncludedInTier
                });
              } else if (productId === 'voice_traffic') {
                if (product.use_custom_amount) {
                  // Custom Amount Mode - single item
                  const label = `${t('calculator.voiceTrafficCustomAmount', 'Deposit for outbound calls')} (${product.custom_amount || voiceTrafficCustomAmount})`;
                  
                  summaryItems.push({
                    label,
                    price: finalPrice, // Voice traffic doesn't get bundle discounts
                    productType: 'voice-traffic',
                    useCustomAmount: true,
                    customAmount: (product.custom_amount || voiceTrafficCustomAmount),
                    minutes: 0,
                    countries: []
                  });
                  summaryItemsWithDiscounts.push({
                    label,
                    price: finalPrice,
                    productType: 'voice-traffic',
                    useCustomAmount: true,
                    customAmount: (product.custom_amount || voiceTrafficCustomAmount),
                    minutes: 0,
                    countries: [],
                    bundleDiscount: product.bundle_discount || 0
                  });
                } else {
                  // Package Mode - separate items for each country package
                  const voiceTrafficCountries = product.voice_traffic_countries || {};
                  
                  if (Object.keys(voiceTrafficCountries).length > 0) {
                    // Create separate summary items for each country package
                    Object.entries(voiceTrafficCountries).forEach(([countryCode, countryData]) => {
                      // Get requested minutes for this country for overage calculation
                      const requestedMinutes = voiceTrafficCountriesMinutes[countryCode] || countryData.minutes || 0;
                      
                      // Create structured data for the new formatVoiceTrafficLabel function
                      const packageData = {
                        countryCode: countryCode,
                        minutes: countryData.minutes || 0,
                        priceLocal: countryData.price_local || 0,
                        currency: countryData.currency || 'USD',
                        packageName: countryData.package_name, // Fallback for legacy parsing
                        overageRate: countryData.overage_rate || countryData.price_local || 0
                      };
                      
                      // Create different labels for different contexts
                      const titleLabel = formatVoiceTrafficLabel(packageData, 'title', t, { requestedMinutes });
                      const detailedLabel = formatVoiceTrafficLabel(packageData, 'detailed', t, { requestedMinutes });
                      
                      // Create metadata with overage using Bundle API data  
                      const baseMinutes = countryData.minutes || 0;
                      const overageMinutes = countryData.overage_minutes || 0;
                      let metadataLabel = `${baseMinutes} ${t('calculator.voiceTrafficMinutesUnit', 'хвилин')}`;
                      if (overageMinutes > 0) {
                        metadataLabel += ` + ${overageMinutes} ${t('calculator.voiceTrafficOverageText', 'поза пакетом')}`;
                      }
                      
                      
                      
                      summaryItems.push({
                        label: titleLabel, // Use title format for product name: "Пакет хвилин Україна"
                        metadata: metadataLabel, // Volume info with overage: "1000 хвилин + 300 поза пакетом"
                        price: countryData.total_cost_usd || countryData.price_usd || 0, // Use total cost with overage first
                        productType: 'voice-traffic',
                        useCustomAmount: false,
                        customAmount: 0,
                        minutes: countryData.minutes || 0,
                        countries: [countryCode],
                        countryCode: countryCode,
                        packageName: countryData.package_name || '',
                        packageId: countryData.package_id || '',
                        priceLocal: countryData.price_local || 0,
                        currency: countryData.currency || 'USD',
                        // Add additional data for OfferPage detailed display
                        detailedLabel: detailedLabel,
                        // NEW: Use Bundle API data directly
                        requestedMinutes: countryData.requested_minutes || requestedMinutes,
                        overageMinutes: countryData.overage_minutes || 0,
                        pricePerMinuteLocal: countryData.price_per_minute_local || 0,
                        pricePerMinuteUSD: countryData.price_per_minute_usd || 0,
                        overageRateLocal: countryData.overage_rate_local || 0,
                        overageRateUSD: countryData.overage_rate_usd || 0,
                        overageCostUSD: countryData.overage_cost_usd || 0
                      });
                      summaryItemsWithDiscounts.push({
                        label: titleLabel, // Use title format for product name: "Пакет хвилин Україна"
                        metadata: metadataLabel, // Volume info with overage: "1000 хвилин + 300 поза пакетом"
                        price: countryData.total_cost_usd || countryData.price_usd || 0, // Use total cost with overage first
                        productType: 'voice-traffic',
                        useCustomAmount: false,
                        customAmount: 0,
                        minutes: countryData.minutes || 0,
                        countries: [countryCode],
                        countryCode: countryCode,
                        packageName: countryData.package_name || '',
                        packageId: countryData.package_id || '',
                        priceLocal: countryData.price_local || 0,
                        currency: countryData.currency || 'USD',
                        bundleDiscount: product.bundle_discount || 0,
                        // Add additional data for OfferPage detailed display
                        detailedLabel: detailedLabel,
                        // NEW: Use Bundle API data directly
                        requestedMinutes: countryData.requested_minutes || requestedMinutes,
                        overageMinutes: countryData.overage_minutes || 0,
                        pricePerMinuteLocal: countryData.price_per_minute_local || 0,
                        pricePerMinuteUSD: countryData.price_per_minute_usd || 0,
                        overageRateLocal: countryData.overage_rate_local || 0,
                        overageRateUSD: countryData.overage_rate_usd || 0,
                        overageCostUSD: countryData.overage_cost_usd || 0
                      });
                    });
                  } else {
                    // No country data returned by Bundle API: do not add phantom summary items
                  }
                }
              } else if (productId === 'omnichannel_chat') {
                // For Pro/Corporate tiers, Chat is included - show simple label
                // Advanced tier: Chat is a paid add-on (NOT included)
                const isChatIncludedInTier = packageTier === 'pro' || packageTier === 'corporate';
                const isBasicTierChat = packageTier === 'basic' || packageTier === 'advanced';

                // Calculate effective Chat users for Basic tier (= Business Phone users, FMC excluded)
                const effectiveChatUsers = getEffectiveChatUsers({ businessPhoneUsers, businessPhoneType, packageTier });

                // Label: Pro/Corporate = no quantity, Basic = users (locked), Legacy = users
                let chatLabel;
                if (isChatIncludedInTier) {
                  chatLabel = t('calculator.omnichannelChat', 'Omnichannel Chat');
                } else if (isBasicTierChat) {
                  chatLabel = `${t('calculator.omnichannelChat', 'Omnichannel Chat')} (${effectiveChatUsers} ${t('calculator.chatUsers', 'users')})`;
                } else {
                  chatLabel = `${t('calculator.omnichannelChat', 'Omnichannel Chat')} (${omnichannelChatUsers} ${t('calculator.chatUsers', 'users')})`;
                }

                const chatUsersCount = isBasicTierChat ? effectiveChatUsers : omnichannelChatUsers;

                summaryItems.push({
                  label: chatLabel,
                  price: finalPrice,
                  productType: 'omnichannel-chat',
                  users: chatUsersCount,
                  includedInTier: isChatIncludedInTier
                });
                summaryItemsWithDiscounts.push({
                  label: chatLabel,
                  price: finalPrice,
                  productType: 'omnichannel-chat',
                  users: chatUsersCount,
                  bundleDiscount: product.bundle_discount || 0,
                  includedInTier: isChatIncludedInTier
                });
              } else if (productId === 'eniq_ai' && isEniqPerUserMode()) {
                // Per-user model: Eniq users follow Business Phone / FMC users.
                // The local fallback needs a phone anchor, same as the gate in
                // usePricingSnapshot — a stale pro/corporate tier without BP
                // must not label Eniq as included (backend prices it standalone).
                const eniqIncluded = product.included_in_tier ||
                  (isProductSelected('business-phone') && isEniqIncludedInTier(eniqTier, packageTier));
                const eniqUsers = product.users || 0;
                const resolvedEniqTier = normalizeEniqTier(product.eniq_tier || eniqTier);
                const eniqTierName = getEniqTierDisplayName(resolvedEniqTier);

                const eniqLabel = eniqIncluded
                  ? `${t('calculator.eniqAiLabel', 'Eniq AI')} ${eniqTierName}`
                  : `${t('calculator.eniqAiLabel', 'Eniq AI')} ${eniqTierName} (${eniqUsers} ${t('calculator.businessPhoneUsers', 'users')})`;

                const eniqItem = {
                  label: eniqLabel,
                  price: finalPrice, // Eniq AI doesn't get bundle discounts
                  productType: 'eniq-ai',
                  eniqTier: resolvedEniqTier,
                  users: eniqUsers,
                  pricePerUser: product.price_per_user || 0,
                  includedInTier: eniqIncluded
                };

                summaryItems.push({ ...eniqItem });
                summaryItemsWithDiscounts.push({ ...eniqItem, bundleDiscount: product.bundle_discount || 0 });
              } else if (productId === 'eniq_ai') {
                // Legacy per-minute model (conserved)
                // Get plan details from bundle product
                const selectedPlan = product.selected_plan || 'start';
                const includedMinutes = product.included_minutes || 0;
                const extraMinutes = product.extra_minutes || 0;
                const includedUnits = product.included_units || 0;
                const extraUnits = product.extra_units || 0;
                const pricePerUnit = product.price_per_unit || 0;
                const planName = getPlanName(selectedPlan, t);
                // Get units from backend response
                const totalUnits = Math.floor(product.total_units || eniqAiMinutes);
                
                summaryItems.push({
                  label: `${t('calculator.eniqAiLabel', 'Eniq AI')} (${totalUnits.toLocaleString()} ${t('calculator.eniqAiUnits', 'units')} ${planName})`,
                  price: finalPrice, // Eniq AI doesn't get bundle discounts  
                  productType: 'eniq-ai',
                  minutes: eniqAiMinutes,
                  premium_models: eniqAiPremiumModels,
                  selected_plan: selectedPlan,
                  included_minutes: includedMinutes,
                  extra_minutes: extraMinutes,
                  included_units: includedUnits,
                  extra_units: extraUnits,
                  price_per_unit: pricePerUnit,
                  units: totalUnits
                });
                summaryItemsWithDiscounts.push({
                  label: `${t('calculator.eniqAiLabel', 'Eniq AI')} (${totalUnits.toLocaleString()} ${t('calculator.eniqAiUnits', 'units')} ${planName})`,
                  price: finalPrice,
                  productType: 'eniq-ai',
                  minutes: eniqAiMinutes,
                  premium_models: eniqAiPremiumModels,
                  selected_plan: selectedPlan,
                  included_minutes: includedMinutes,
                  extra_minutes: extraMinutes,
                  included_units: includedUnits,
                  extra_units: extraUnits,
                  price_per_unit: pricePerUnit,
                  units: totalUnits,
                  bundleDiscount: product.bundle_discount || 0
                });
              } else if (productId === 'voicebot') {
                // Get plan details from bundle product
                const selectedPlan = product.selected_plan || 'lite';
                const includedMinutes = product.included_minutes || 0;
                const extraMinutes = product.extra_minutes || 0;
                const includedUnits = product.included_units || 0;
                const extraUnits = product.extra_units || 0;
                const pricePerUnit = product.price_per_unit || 0;
                const planName = getPlanName(selectedPlan, t);
                const totalUnits = Math.floor(product.total_units || voicebotMinutes);

                summaryItems.push({
                  label: `${t('calculator.voicebotLabel', 'VoiceBot')} (${totalUnits.toLocaleString()} ${t('calculator.voicebotMinutesUnit', 'min')} ${planName})`,
                  price: finalPrice,
                  productType: 'voicebot',
                  minutes: voicebotMinutes,
                  premium_models: voicebotAiMode,
                  selected_plan: selectedPlan,
                  included_minutes: includedMinutes,
                  extra_minutes: extraMinutes,
                  included_units: includedUnits,
                  extra_units: extraUnits,
                  price_per_unit: pricePerUnit,
                  units: totalUnits
                });
                summaryItemsWithDiscounts.push({
                  label: `${t('calculator.voicebotLabel', 'VoiceBot')} (${totalUnits.toLocaleString()} ${t('calculator.voicebotMinutesUnit', 'min')} ${planName})`,
                  price: finalPrice,
                  productType: 'voicebot',
                  minutes: voicebotMinutes,
                  premium_models: voicebotAiMode,
                  selected_plan: selectedPlan,
                  included_minutes: includedMinutes,
                  extra_minutes: extraMinutes,
                  included_units: includedUnits,
                  extra_units: extraUnits,
                  price_per_unit: pricePerUnit,
                  units: totalUnits,
                  bundleDiscount: product.bundle_discount || 0
                });
              }
            });

            // Get total from bundle response
            totalUSD = bundleResult.total.primary.amount;
            
          } catch (error) {
            console.error('Failed to calculate bundle pricing:', error);
            // Clear summary items by updating state directly when bundle calculation fails
            setPriceCalculation({
              currency: getCurrencySymbol(region),
              businessPhonePrice: 0,
              callTrackingPrice: 0,
              aiPrice: 0,
              callbackPrice: 0,
              phoneNumbersTotal: 0,
              voiceTrafficPrice: 0,
              totalMonthlyCost: 0,
              summaryItems: [],
              summaryItemsForSubscription: [],
              subscriptionPrices: {
                monthly: 0,
                annual: 0,
                semiAnnual: 0,
                quarterly: 0,
                twelveMonthContract: 0,
                splitPayment: 0
              },
              total: 0,
              phoneNumbersNeeded: { total: 0 }
            });
            return; // Exit early to prevent executing rest of the function
          }
        } else {
        }

          // Phone Numbers – always calculated separately (manual pricing).
          // Phone catalog prices come in mixed currencies (EUR/USD); on insert
          // we convert to USD-internal (see PhoneNumbers/index.jsx). Here we
          // normalize per-region so summaryItems carry priceCurrency and the
          // accumulator stays in the region's pricing currency (EUR for EU).
          if (isProductSelected('phone-numbers') && phoneNumbers.length > 0) {
            const phoneItems = buildPhoneNumberPricingItems({ phoneNumbers, t });
            phoneItems.forEach(item => {
              totalUSD += normalizePhoneLineForTotal(item.price, region, currencyRates);
              summaryItems.push(item);
              summaryItemsWithDiscounts.push({ ...item, bundleDiscount: 0 });
            });
          }

          // Normalize all items to region pricing currency so consumers get a
          // uniform contract: `price` matches `priceCurrency`, no guessing.
          const normalize = (item) => normalizePricingItemForRegion(item, region, currencyRates);
          for (let i = 0; i < summaryItems.length; i++) summaryItems[i] = normalize(summaryItems[i]);
          for (let i = 0; i < summaryItemsWithDiscounts.length; i++) summaryItemsWithDiscounts[i] = normalize(summaryItemsWithDiscounts[i]);

          // Calculate subscription prices based on total using custom discounts
          const discountFactor360 = 1 + (customDiscount360 / 100);
          const discountFactor180 = 1 + (customDiscount180 / 100);
          const subscriptionPrices = {
            monthly: Math.round(totalUSD),
            annual: Math.round(totalUSD * 12 / discountFactor360), // Custom 360-day discount
            semiAnnual: Math.round(totalUSD * 6 / discountFactor180), // Custom 180-day discount
            quarterly: Math.round(totalUSD * 3), // no discount
            twelveMonthContract: Math.round(totalUSD * 12), // no discount
            splitPayment: Math.round(totalUSD * 12) // no discount
          };

          // Calculate phone numbers needed for call tracking using QSLN formula
          let phoneNumbersNeeded = 0;
          if (isProductSelected('call-tracking')) {
            phoneNumbersNeeded = calculatePhoneNumbersNeeded();
          }

          setPriceCalculation({
            currency,
            businessPhonePrice: individualPrices.businessPhone || 0,
            callTrackingPrice: individualPrices.callTracking || 0,
            aiPrice: individualPrices.ai || 0,
            callbackPrice: individualPrices.callback || 0,
            phoneNumbersTotal: individualPrices.phoneNumbers || 0,
            voiceTrafficPrice: individualPrices.voiceTraffic || 0,
            totalMonthlyCost: totalUSD,
            summaryItems: summaryItemsWithDiscounts, // Use discounted prices for display
            summaryItemsForSubscription: summaryItems, // Use base prices for subscription calculator
            subscriptionPrices,
            total: totalUSD,
            phoneNumbersNeeded: { total: phoneNumbersNeeded }
          });

        } catch (error) {
          console.error('Failed to calculate prices:', error);
          setPriceCalculation({
            currency: getCurrencySymbol(region),
            businessPhonePrice: 0,
            callTrackingPrice: 0,
            aiPrice: 0,
            callbackPrice: 0,
            phoneNumbersTotal: 0,
            voiceTrafficPrice: 0,
            totalMonthlyCost: 0,
            summaryItems: [],
            summaryItemsForSubscription: [],
            subscriptionPrices: {
              monthly: 0,
              annual: 0,
              semiAnnual: 0,
              quarterly: 0,
              twelveMonthContract: 0,
              splitPayment: 0
            },
            total: 0,
            phoneNumbersNeeded: { total: 0 }
          });
        }
      };;;
      calculatePrices();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    region, businessPhoneUsers, callTrackingSessions, aiCalls, callbackSessions,
    voiceTrafficCountriesMinutes, voiceTrafficUseCustomAmount, voiceTrafficCustomAmount,
    omnichannelChatUsers, callTrackingType, staticSources,
    callMeButtonEnabled, businessPhoneType, fmcUsers,
    customDiscount360, customDiscount180,
    eniqAiMinutes, eniqAiPremiumModels, eniqTier, eniqStandaloneUsers,
    voicebotMinutes, voicebotAiMode, voicebotExtraChannels,
    veryCustomProductDiscounts,
    numberFormatsQuantity,
    packageTier, // NEW: Re-calculate when package tier changes
    i18n.language, // Re-calculate when language changes to update labels
    // Stable references using useMemo
    stableSelectedProducts,
    stablePhoneNumbers
  ]);

  // State Aggregation and Reset Logic
  const getCalculatorState = useCallback(() => ({
    // --- SUBSCRIPTION SETTINGS ---
    subscriptionSettings: {
      subscription90Days,
      subscription180Days,
      subscription12Month,
      subscriptionSplitPayment,
    },
    // region is now managed by UserPreferencesContext, not stored in tab data
    products: {
      businessPhoneUsers,
      callTrackingSessions,
      aiCalls,
      callbackSessions,
      voiceTrafficMinutes,
      omnichannelChatUsers,
      manualPackageSelection,
      selectedProducts,
    },
    phoneNumbers,
    callTrackingType,
    callMeButtonEnabled,
    staticSources,
    numberFormatsQuantity,
    hasCustomSettings,
    // nonEnglishAnalysis, // Commented out - unified pricing now
    hasAiCustomSettings,
    businessPhoneType,
    fmcUsers,
    hasBusinessPhoneCustomSettings,
    packageTier, // Package-based pricing tier
    // Duplicated at top level for backward compatibility (can be removed after full migration)
    subscription90Days,
    subscription180Days,
    subscription12Month,
    subscriptionSplitPayment,
    customDiscount360,
    customDiscount180,
    hasGlobalSettings,
    // Phone Numbers Settings
    enablePhoneActivation,
    hasPhoneNumbersCustomSettings,
    // Eniq AI Settings
    eniqAiMinutes,
    eniqAiPremiumModels,
    eniqTier,
    eniqStandaloneUsers,
    hasEniqAiCustomSettings,
    eniqAiImplementationFee,
    enableEniqImplementationFee,
    // VoiceBot Settings
    voicebotMinutes,
    voicebotAiMode,
    hasVoicebotCustomSettings,
    voicebotImplementationFee,
    enableVoicebotImplementationFee,
    voicebotExtraChannels,
    // Voice Traffic Settings
    voiceTrafficCountriesMinutes,
    voiceTrafficUseCustomAmount,
    voiceTrafficCustomAmount,
    hasVoiceTrafficCustomSettings,
    // Very Custom Product Discounts
    veryCustomProductDiscounts,
  }), [
    businessPhoneUsers, callTrackingSessions, aiCalls, callbackSessions, voiceTrafficMinutes,
    omnichannelChatUsers, manualPackageSelection, selectedProducts, phoneNumbers, callTrackingType, callMeButtonEnabled, staticSources,
    hasCustomSettings, /* nonEnglishAnalysis, */ hasAiCustomSettings, businessPhoneType, fmcUsers,
    hasBusinessPhoneCustomSettings, packageTier, subscription90Days, subscription180Days, subscription12Month,
    subscriptionSplitPayment, customDiscount360, customDiscount180, hasGlobalSettings, enablePhoneActivation, hasPhoneNumbersCustomSettings,
    eniqAiMinutes, eniqAiPremiumModels, eniqTier, eniqStandaloneUsers, hasEniqAiCustomSettings, eniqAiImplementationFee, enableEniqImplementationFee,
    voicebotMinutes, voicebotAiMode, hasVoicebotCustomSettings, voicebotImplementationFee, enableVoicebotImplementationFee, voicebotExtraChannels,
    voiceTrafficCountriesMinutes, voiceTrafficUseCustomAmount, voiceTrafficCustomAmount, hasVoiceTrafficCustomSettings,
    veryCustomProductDiscounts
  ]);

  const resetAllStateFromTabData = useCallback((data) => {
    // Handle both legacy flattened structure and new nested `subscriptionSettings` object
    const subscriptionSrc = (data && data.subscriptionSettings) || data || {};
    const d = data || {};
    const p = d.products || {};

    // region is now managed by UserPreferencesContext
    setCallTrackingType(d.callTrackingType || 'dynamic');
    setCallMeButtonEnabled(d.callMeButtonEnabled || false);
    setStaticSources(d.staticSources !== undefined ? d.staticSources : 0);
    setHasCustomSettings(d.hasCustomSettings || false);
    setNonEnglishAnalysis(d.nonEnglishAnalysis || false);
    setHasAiCustomSettings(d.hasAiCustomSettings || false);
    setBusinessPhoneType(d.businessPhoneType || 'business-phone');
    setFmcUsers(d.fmcUsers !== undefined ? d.fmcUsers : 0);
    setHasBusinessPhoneCustomSettings(d.hasBusinessPhoneCustomSettings || false);
    setPackageTier(d.packageTier || 'basic');
    setSubscription90Days(subscriptionSrc.subscription90Days || false);
    setSubscription180Days(subscriptionSrc.subscription180Days || false);
    setSubscription12Month(subscriptionSrc.subscription12Month || false);
    setSubscriptionSplitPayment(
      subscriptionSrc.subscriptionSplitPayment !== undefined
        ? subscriptionSrc.subscriptionSplitPayment
        : region === 'ua'
    );
    setCustomDiscount360(d.customDiscount360 ?? 20);
    setCustomDiscount180(d.customDiscount180 ?? 0);
    setHasGlobalSettings(d.hasGlobalSettings || false);
    setEnablePhoneActivation(d.enablePhoneActivation || false);
    setHasPhoneNumbersCustomSettings(d.hasPhoneNumbersCustomSettings || false);
    setVeryCustomProductDiscounts(d.veryCustomProductDiscounts || {});
    
    // Set Eniq AI state
    setEniqAiMinutes(d.eniqAiMinutes || 2000);
    setEniqAiPremiumModels(d.eniqAiPremiumModels !== undefined ? d.eniqAiPremiumModels : true);
    setEniqTier(normalizeEniqTier(d.eniqTier));
    setEniqStandaloneUsers(d.eniqStandaloneUsers ?? ENIQ_MIN_BILLABLE_USERS);
    setHasEniqAiCustomSettings(d.hasEniqAiCustomSettings || false);
    setEniqAiImplementationFee(d.eniqAiImplementationFee || 0);
    setEnableEniqImplementationFee(d.enableEniqImplementationFee || false);

    // Set VoiceBot state
    setVoicebotMinutes(Math.max(d.voicebotMinutes || 1100, 1100));
    setVoicebotAiMode(d.voicebotAiMode !== undefined ? d.voicebotAiMode : true);
    setHasVoicebotCustomSettings(d.hasVoicebotCustomSettings || false);
    setVoicebotImplementationFee(d.voicebotImplementationFee || 0);
    setEnableVoicebotImplementationFee(d.enableVoicebotImplementationFee || false);
    setVoicebotExtraChannels(d.voicebotExtraChannels || 0);

    // Set Voice Traffic state with region-based defaults
    const defaultVTSettings = getDefaultVoiceTrafficSettings(region);
    setVoiceTrafficCountriesMinutes(d.voiceTrafficCountriesMinutes || defaultVTSettings.voiceTrafficCountriesMinutes);
    setVoiceTrafficUseCustomAmount(d.voiceTrafficUseCustomAmount !== undefined ? d.voiceTrafficUseCustomAmount : defaultVTSettings.voiceTrafficUseCustomAmount);
    setVoiceTrafficCustomAmount(d.voiceTrafficCustomAmount !== undefined ? d.voiceTrafficCustomAmount : defaultVTSettings.voiceTrafficCustomAmount);
    setHasVoiceTrafficCustomSettings(d.hasVoiceTrafficCustomSettings || false);
    
    // Reset hook states with proper data
    productsReset(p);
    phoneNumbersReset(d.phoneNumbers);
    
    // FIXED: Improved auto-activation logic for Voice Traffic (region-aware)
    // Only auto-activate if settings differ from region-based defaults
    const vtDefaults = getDefaultVoiceTrafficSettings(region);
    const hasCountryMinutesDiff = d.voiceTrafficCountriesMinutes 
      ? JSON.stringify(d.voiceTrafficCountriesMinutes) !== JSON.stringify(vtDefaults.voiceTrafficCountriesMinutes)
      : false;
    const hasUseCustomDiff = (d.voiceTrafficUseCustomAmount !== undefined)
      ? d.voiceTrafficUseCustomAmount !== vtDefaults.voiceTrafficUseCustomAmount
      : false;
    const hasCustomAmountDiff = (d.voiceTrafficCustomAmount !== undefined)
      ? d.voiceTrafficCustomAmount !== vtDefaults.voiceTrafficCustomAmount && d.voiceTrafficCustomAmount > 0
      : false;
    const hasVoiceTrafficData = hasCountryMinutesDiff || hasUseCustomDiff || hasCustomAmountDiff;
    
    // Check if Voice Traffic is explicitly selected in products data
    const isVoiceTrafficExplicitlySelected = p.selectedProducts && 
      p.selectedProducts.some(product => product.id === 'voice-traffic' && product.active);
    
    if (hasVoiceTrafficData && isVoiceTrafficExplicitlySelected) {
      // Only auto-activate if both data exists AND it's explicitly selected in the products
      setTimeout(() => {
        if (!isProductSelected('voice-traffic')) {
          toggleProduct('voice-traffic');
        }
      }, 0);
    }
  }, [productsReset, phoneNumbersReset, isProductSelected, toggleProduct, region]);

  // Effect to initialize/clear Voice Traffic data when product is activated/deactivated
  useEffect(() => {
    const isVoiceTrafficActive = isProductSelected('voice-traffic');
    
    if (isVoiceTrafficActive) {
      // Check if Voice Traffic is being activated for the first time (no custom settings)
      const regionDefaults = getDefaultVoiceTrafficSettings(region);
      const hasDefaultSettings = !hasVoiceTrafficCustomSettings && 
        JSON.stringify(voiceTrafficCountriesMinutes) === JSON.stringify(regionDefaults.voiceTrafficCountriesMinutes) &&
        voiceTrafficUseCustomAmount === regionDefaults.voiceTrafficUseCustomAmount && 
        voiceTrafficCustomAmount === regionDefaults.voiceTrafficCustomAmount;
      
      if (hasDefaultSettings) {
        // Initialize with region-based defaults when activated for the first time
        const defaultVTSettings = getDefaultVoiceTrafficSettings(region);
        
        // Only update if the current settings don't match the default settings for this region
        const shouldUpdate = JSON.stringify(voiceTrafficCountriesMinutes) !== JSON.stringify(defaultVTSettings.voiceTrafficCountriesMinutes) ||
          voiceTrafficUseCustomAmount !== defaultVTSettings.voiceTrafficUseCustomAmount ||
          voiceTrafficCustomAmount !== defaultVTSettings.voiceTrafficCustomAmount;
        
        if (shouldUpdate) {
          setVoiceTrafficCountriesMinutes(defaultVTSettings.voiceTrafficCountriesMinutes);
          setVoiceTrafficUseCustomAmount(defaultVTSettings.voiceTrafficUseCustomAmount);
          setVoiceTrafficCustomAmount(defaultVTSettings.voiceTrafficCustomAmount);
        }
      }
    } else {
      // If Voice Traffic is deactivated, clear its data ONLY if it differs from region defaults
      const defaults = getDefaultVoiceTrafficSettings(region);
      const isCountriesDefault = JSON.stringify(voiceTrafficCountriesMinutes) === JSON.stringify(defaults.voiceTrafficCountriesMinutes);
      const isUseCustomDefault = voiceTrafficUseCustomAmount === defaults.voiceTrafficUseCustomAmount;
      const isCustomAmountDefault = voiceTrafficCustomAmount === defaults.voiceTrafficCustomAmount;
      const isExactlyAtDefaults = isCountriesDefault && isUseCustomDefault && isCustomAmountDefault && !hasVoiceTrafficCustomSettings;

      if (!isExactlyAtDefaults) {
        setVoiceTrafficCountriesMinutes(defaults.voiceTrafficCountriesMinutes);
        setVoiceTrafficUseCustomAmount(defaults.voiceTrafficUseCustomAmount);
        setVoiceTrafficCustomAmount(defaults.voiceTrafficCustomAmount);
        setHasVoiceTrafficCustomSettings(false);
      }
    }
  }, [region, isProductSelected, voiceTrafficCountriesMinutes, voiceTrafficCustomAmount, voiceTrafficUseCustomAmount, hasVoiceTrafficCustomSettings]);

  // Main Effects for State Synchronization
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!instanceTabId) return;

    // Only reset state if tabData has meaningful content
    // This prevents resetting to defaults when a new tab is created
    // but tabData hasn't been loaded yet
    if (tabData && Object.keys(tabData).length > 0) {
      resetAllStateFromTabData(tabData);
      initialCalculatorStateRef.current = JSON.stringify(getCalculatorState());
      isInitialMountRef.current = true;
    }
  }, [instanceTabId]);

  const stateToWatch = useMemo(() => JSON.stringify(getCalculatorState()), [getCalculatorState]);

  useEffect(() => {
    if (isInitialMountRef.current) {
      
      isInitialMountRef.current = false;
      return;
    }

    if (initialCalculatorStateRef.current === stateToWatch) {
      
      return;
    }

    // Debounced save to prevent cycling during rapid slider changes
    const timeoutId = setTimeout(() => {
      const stateToSave = JSON.parse(stateToWatch);
      onStateSave(stateToSave);
      initialCalculatorStateRef.current = stateToWatch;
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [stateToWatch, instanceTabId, onStateSave]);

  const hasValuesChanged = useCallback(() => {
    return stateToWatch !== initialCalculatorStateRef.current;
  }, [stateToWatch]);

  // Update current calculator state for duplication
  useEffect(() => {
    const currentState = getCalculatorState();
    updateCurrentCalculatorState(currentState);
  }, [getCalculatorState, updateCurrentCalculatorState]);

  // Event Handlers
  const handleApplyCallTrackingSettings = (settings) => {
    if (settings) {
      setCallTrackingType(settings.callTrackingType);
      setCallMeButtonEnabled(settings.callMeButtonEnabled);
      setStaticSources(settings.staticSources);
      setNumberFormatsQuantity(settings.numberFormatsQuantity || 1);
      setHasCustomSettings(settings.hasCustomSettings);
    }
    setCallTrackingSettingsOpen(false);
  };
  const handleApplyAISettings = (settings) => {
    if (settings) {
      setNonEnglishAnalysis(settings.nonEnglishAnalysis);
      setHasAiCustomSettings(settings.hasAiCustomSettings);
    }
    setAiSettingsOpen(false);
  };
  const handleApplyBusinessPhoneSettings = (settings) => {
    if (settings) {
      setBusinessPhoneType(settings.businessPhoneType);
      setBusinessPhoneUsers(settings.businessPhoneUsers);
      setFmcUsers(settings.fmcUsers);
      setHasBusinessPhoneCustomSettings(settings.hasBusinessPhoneCustomSettings);
    }
    setBusinessPhoneSettingsOpen(false);
  };
  const handleApplyGlobalSettings = (settings) => {
    if (settings) {
      if (settings.showAdvancedTier !== undefined) {
        setShowAdvancedTier(settings.showAdvancedTier);
        localStorage.setItem('showAdvancedTier', String(settings.showAdvancedTier));
      }
      if (settings.showPricesWithoutBonuses !== undefined) {
        setShowPricesWithoutBonuses(settings.showPricesWithoutBonuses);
        localStorage.setItem('showPricesWithoutBonuses', String(settings.showPricesWithoutBonuses));
      }
      if (settings.hasGlobalSettings !== undefined) {
        setHasGlobalSettings(settings.hasGlobalSettings);
      }
    }
    setGlobalSettingsOpen(false);
  };

  const handleApplyPhoneNumbersSettings = (settings) => {
    if (settings) {
      setEnablePhoneActivation(settings.enablePhoneActivation);
      setHasPhoneNumbersCustomSettings(settings.hasPhoneNumbersCustomSettings);
    }
    setPhoneNumbersSettingsOpen(false);
  };

  const handleApplyEniqAISettings = (settings) => {
    if (settings) {
      setEniqAiPremiumModels(settings.eniqAiPremiumModels);
      setHasEniqAiCustomSettings(settings.hasEniqAiCustomSettings);
      if (settings.eniqAiImplementationFee !== undefined) {
        setEniqAiImplementationFee(settings.eniqAiImplementationFee);
      }
      if (settings.enableEniqImplementationFee !== undefined) {
        setEnableEniqImplementationFee(settings.enableEniqImplementationFee);
      }
    }
    setEniqAiSettingsOpen(false);
  };

  const handleApplyVoicebotSettings = (settings) => {
    if (settings) {
      setVoicebotAiMode(settings.voicebotAiMode);
      setHasVoicebotCustomSettings(settings.hasVoicebotCustomSettings);
      if (settings.voicebotImplementationFee !== undefined) {
        setVoicebotImplementationFee(settings.voicebotImplementationFee);
      }
      if (settings.enableVoicebotImplementationFee !== undefined) {
        setEnableVoicebotImplementationFee(settings.enableVoicebotImplementationFee);
      }
      if (settings.voicebotExtraChannels !== undefined) {
        setVoicebotExtraChannels(settings.voicebotExtraChannels);
      }
    }
    setVoicebotSettingsOpen(false);
  };

  // Handler for product changes (used by EniqAI and other products)
  const handleProductChange = useCallback((productId, isActive, data = {}) => {
    // Toggle product if needed
    if (isProductSelected(productId) !== isActive) {
      toggleProduct(productId);
    }
    
    // Handle specific product data updates
    if (productId === 'eniq-ai' && data) {
      // The pricing data is already calculated by the EniqAI component
      // We don't need to store it in the context as it's calculated on-demand
    }
  }, [isProductSelected, toggleProduct]);

  const handleApplySubscriptionSettings = (settings) => {
    if (settings) {
      setSubscription90Days(settings.subscription90Days);
      setSubscription180Days(settings.subscription180Days);
      setSubscription12Month(settings.subscription12Month);
      setSubscriptionSplitPayment(settings.subscriptionSplitPayment);
      setCustomDiscount360(settings.customDiscount360);
      setCustomDiscount180(settings.customDiscount180);
      setHasGlobalSettings(settings.hasGlobalSettings);
    }
    setSubscriptionSettingsOpen(false);
  };

  // Handler for very custom product discounts
  const handleUpdateVeryCustomProductDiscount = useCallback((productId, discount) => {
    setVeryCustomProductDiscounts(prev => {
      const updated = { ...prev };
      if (discount === null || discount === undefined || discount === 0) {
        // Remove discount if null/undefined/0
        delete updated[productId];
      } else {
        // Set discount (0-100 range)
        updated[productId] = Math.max(0, Math.min(100, discount));
      }
      return updated;
    });
  }, []);

  // Compute hasCustomSettings with very custom discounts
  const hasCallTrackingAnyCustomSettings = useMemo(() => {
    return hasCustomSettings || (veryCustomProductDiscounts['call_tracking'] && veryCustomProductDiscounts['call_tracking'] > 0);
  }, [hasCustomSettings, veryCustomProductDiscounts]);

  // Wrap productOptions to hide Omnichannel Chat in FMC-only mode (no smartphone app)
  const filteredProductOptions = useCallback(() => {
    const allOptions = productOptions();
    if (businessPhoneType === 'fmc') {
      return allOptions.filter(p => p.id !== 'omnichannel-chat');
    }
    return allOptions;
  }, [productOptions, businessPhoneType]);

  // Context Value
  const contextValue = useMemo(() => ({
    summaryRef, summaryStyle: scrollPosition.style,
    region, // region is read-only from UserPreferencesContext
    businessPhoneUsers, setBusinessPhoneUsers,
    callTrackingSessions, setCallTrackingSessions,
    aiCalls, setAiCalls,
    callbackSessions, setCallbackSessions,
    voiceTrafficMinutes, setVoiceTrafficMinutes,
    omnichannelChatUsers, setOmnichannelChatUsers,
    manualPackageSelection, setManualPackageSelection,
    selectedProducts, isProductSelected, toggleProduct, productOptions: filteredProductOptions, selectedProductsData,
    phoneNumbers, setPhoneNumbers, addPhoneNumber, removePhoneNumber, updatePhoneNumber,
    callTrackingSettingsOpen, setCallTrackingSettingsOpen,
    callTrackingType, setCallTrackingType,
    callMeButtonEnabled, setCallMeButtonEnabled,
    staticSources, setStaticSources,
    numberFormatsQuantity, setNumberFormatsQuantity,
    hasCustomSettings, setHasCustomSettings,
    hasCallTrackingAnyCustomSettings,
    // NEW: Separate pricing and display calculations
    calculatePricingSlots, // For backend API pricing
    calculateActualPhoneNumbers, // For UI display and phone-numbers product
    calculatePhoneNumbersNeeded, // Legacy compatibility (uses actualPhoneNumbers)
    handleApplyCallTrackingSettings,
    aiSettingsOpen, setAiSettingsOpen,
    nonEnglishAnalysis, setNonEnglishAnalysis,
    hasAiCustomSettings, setHasAiCustomSettings,
    handleApplyAISettings,
    businessPhoneSettingsOpen, setBusinessPhoneSettingsOpen,
    businessPhoneType, setBusinessPhoneType,
    fmcUsers, setFmcUsers,
    hasBusinessPhoneCustomSettings, setHasBusinessPhoneCustomSettings,
    packageTier, setPackageTier,
    handleApplyBusinessPhoneSettings,
    globalSettingsOpen, setGlobalSettingsOpen,
    showAdvancedTier,
    showPricesWithoutBonuses,
    subscriptionSettingsOpen, setSubscriptionSettingsOpen,
    subscriptionSettings, // Pass the whole settings object
    subscription90Days, subscription180Days, subscription12Month, subscriptionSplitPayment,
    customDiscount360, customDiscount180,
    hasGlobalSettings, setHasGlobalSettings,
    handleApplyGlobalSettings,
    handleApplySubscriptionSettings,
    // Phone Numbers Settings
    phoneNumbersSettingsOpen, setPhoneNumbersSettingsOpen,
    enablePhoneActivation, setEnablePhoneActivation,
    hasPhoneNumbersCustomSettings, setHasPhoneNumbersCustomSettings,
    handleApplyPhoneNumbersSettings,
    // Eniq AI Settings
    eniqAiMinutes, setEniqAiMinutes,
    eniqAiPremiumModels, setEniqAiPremiumModels,
    eniqTier, setEniqTier,
    eniqStandaloneUsers, setEniqStandaloneUsers,
    eniqAiSettingsOpen, setEniqAiSettingsOpen,
    hasEniqAiCustomSettings, setHasEniqAiCustomSettings,
    eniqAiImplementationFee, setEniqAiImplementationFee,
    enableEniqImplementationFee, setEnableEniqImplementationFee,
    handleApplyEniqAISettings,
    // VoiceBot Settings
    voicebotMinutes, setVoicebotMinutes,
    voicebotAiMode, setVoicebotAiMode,
    voicebotSettingsOpen, setVoicebotSettingsOpen,
    hasVoicebotCustomSettings, setHasVoicebotCustomSettings,
    voicebotImplementationFee, setVoicebotImplementationFee,
    enableVoicebotImplementationFee, setEnableVoicebotImplementationFee,
    voicebotExtraChannels, setVoicebotExtraChannels,
    handleApplyVoicebotSettings,
    handleProductChange,
    // Voice Traffic Settings
    voiceTrafficCountriesMinutes, setVoiceTrafficCountriesMinutes,
    voiceTrafficUseCustomAmount, setVoiceTrafficUseCustomAmount,
    voiceTrafficCustomAmount, setVoiceTrafficCustomAmount,
    voiceTrafficSettingsOpen, setVoiceTrafficSettingsOpen,
    hasVoiceTrafficCustomSettings, setHasVoiceTrafficCustomSettings,
    updateVoiceTrafficCountryMinutes,
    updateVoiceTrafficUseCustomAmount,
    updateVoiceTrafficCustomAmount,
    updateVoiceTrafficCountriesMinutes,
    addVoiceTrafficCountry,
    removeVoiceTrafficCountry,
    getVoiceTrafficCountries,
    getVoiceTrafficTotalMinutes,
    getVoiceTrafficCustomCurrency,
    getCountryName,
    // Very Custom Product Discounts
    veryCustomProductDiscounts, 
    handleUpdateVeryCustomProductDiscount,
    ...priceCalculation,
    instanceTabId,
    getCalculatorState,
    hasValuesChanged,
    priceCalculation
  }), [
    scrollPosition.style, region, businessPhoneUsers, callTrackingSessions, aiCalls, callbackSessions, voiceTrafficMinutes, omnichannelChatUsers,
    manualPackageSelection, selectedProducts, isProductSelected, toggleProduct, filteredProductOptions, selectedProductsData,
    phoneNumbers, addPhoneNumber, removePhoneNumber, updatePhoneNumber, callTrackingSettingsOpen, callTrackingType,
    callMeButtonEnabled, staticSources, hasCustomSettings, hasCallTrackingAnyCustomSettings,
    calculatePricingSlots, calculateActualPhoneNumbers, calculatePhoneNumbersNeeded, // NEW functions
    aiSettingsOpen, nonEnglishAnalysis,
    hasAiCustomSettings, businessPhoneSettingsOpen, businessPhoneType, fmcUsers, hasBusinessPhoneCustomSettings,
    packageTier, // Package-based pricing tier
    globalSettingsOpen, showAdvancedTier, showPricesWithoutBonuses, subscriptionSettingsOpen, subscriptionSettings, hasGlobalSettings, handleApplySubscriptionSettings,
    instanceTabId, getCalculatorState, hasValuesChanged,
    subscription90Days, subscription180Days, subscription12Month, subscriptionSplitPayment, customDiscount360, customDiscount180,
    phoneNumbersSettingsOpen, enablePhoneActivation, hasPhoneNumbersCustomSettings, handleApplyPhoneNumbersSettings,
    eniqAiMinutes, eniqAiPremiumModels, eniqTier, eniqStandaloneUsers, eniqAiSettingsOpen, hasEniqAiCustomSettings, eniqAiImplementationFee, enableEniqImplementationFee, handleApplyEniqAISettings,
    voicebotMinutes, voicebotAiMode, voicebotSettingsOpen, hasVoicebotCustomSettings, voicebotImplementationFee, enableVoicebotImplementationFee, voicebotExtraChannels, handleApplyVoicebotSettings,
    handleProductChange,
    veryCustomProductDiscounts, handleUpdateVeryCustomProductDiscount,
    getCountryName,
    priceCalculation
  ]);

  return (
    <CalculatorContext.Provider value={contextValue}>
      {children}
    </CalculatorContext.Provider>
  );
};


export const useCalculator = () => {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  return context;
};

export default CalculatorContext;
